<!-- eslint-disable vue/max-attributes-per-line -->
<!--
  新クロージングページ（日報β専用）。
  ステップ: データセット管理 → 経費管理 → ユーザー集計 → 卓集計 → 店舗集計 → 完了。
  既存 eod-closing は温存し、本ページは別ルート（eod-closing-v2）として新規追加している。
  日計（ユーザー/卓/店舗）はスプレッドシート形式で、集計取得・保存・再集計は
  既存 eod-closing のAPI（useEodClosingV2Aggregations 経由）を流用する。
-->
<template>
  <v-container :key="`eod-closing-v2-${date}`">
    <h1 class="mb-4">
      {{ date }} クロージング
    </h1>

    <!-- 日付選択 -->
    <div class="d-flex align-center flex-wrap mb-4">
      <v-menu
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="date"
            :prepend-icon="icons.mdiCalendar"
            readonly
            hide-details
            style="max-width: 200px;"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          locale="ja"
          :day-format="d => new Date(d).getDate()"
          :max="isProduction ? today : undefined"
          color="primary"
          no-title
        />
      </v-menu>

      <v-btn
        color="secondary"
        small
        class="ml-3"
        :ripple="false"
        @click="date = today"
      >
        本日
      </v-btn>

      <v-btn
        color="info"
        small
        class="ml-2"
        :ripple="false"
        :to="{ name: 'daily-report-summary', query: { date } }"
      >
        日報
      </v-btn>
    </div>

    <!-- ステッパー -->
    <v-stepper
      v-model="step"
      flat
      class="eod-closing-v2-stepper mb-4"
    >
      <v-stepper-header>
        <template v-for="(label, idx) in steps">
          <v-stepper-step
            :key="`step-${idx}`"
            :complete="step > idx"
            editable
            :step="idx"
          >
            {{ label }}
          </v-stepper-step>
          <v-divider
            v-if="idx < steps.length - 1"
            :key="`step-divider-${idx}`"
          />
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <!-- 0: データセット管理 -->
        <v-stepper-content step="0">
          <h2 class="mb-3">
            データセット管理
          </h2>
          <timecards-table :date="date" class="mb-6" />
          <data-tabs :show-date-picker="false" />
        </v-stepper-content>

        <!-- 1: 経費管理 -->
        <v-stepper-content step="1" eager>
          <h2 class="mb-3">
            経費管理
          </h2>
          <expenses-table :date="date" />
        </v-stepper-content>

        <!-- 2: ユーザー集計 -->
        <v-stepper-content step="2" eager>
          <h2 class="mb-3">
            ユーザー集計
          </h2>
          <daily-aggregation-step
            category-label="ユーザー集計"
            :aggregations-data="userAggregationsData"
            :aggregations-map="userAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            :available-users="availableUsers"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
            @fetch-available-users="onFetchAvailableUsers"
            @create-user="onCreateUser"
            @destroy-user="onDestroyUser"
          />
        </v-stepper-content>

        <!-- 3: 卓集計 -->
        <v-stepper-content step="3" eager>
          <h2 class="mb-3">
            卓集計
          </h2>
          <daily-aggregation-step
            category-label="卓集計"
            :aggregations-data="tableAggregationsData"
            :aggregations-map="tableAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
          />
        </v-stepper-content>

        <!-- 4: 店舗集計 -->
        <v-stepper-content step="4" eager>
          <h2 class="mb-3">
            店舗集計
          </h2>
          <daily-aggregation-step
            category-label="店舗集計"
            :aggregations-data="clubAggregationsData"
            :aggregations-map="clubAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
          />
        </v-stepper-content>

        <!-- 5: 完了 -->
        <v-stepper-content step="5">
          <h2 class="mb-3">
            完了
          </h2>
          <v-card
            outlined
            class="pa-6 text-center"
          >
            <v-icon
              color="success"
              size="48"
              class="mb-3"
            >
              {{ icons.mdiCheckCircle }}
            </v-icon>
            <p class="text-base mb-4">
              クロージングの確認が完了したら、日報に戻ってください。
            </p>
            <v-btn
              color="primary"
              x-large
              :ripple="false"
              :to="{ name: 'daily-report-summary', query: { date } }"
            >
              日報に戻る
            </v-btn>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import {
  ref, inject, computed, watch, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import { mdiCalendar, mdiCheckCircle } from '@mdi/js'
import { parseISO, isAfter } from 'date-fns'
import { useRouter } from '@core/utils'
import { useProcessEnv } from '@/views/v2-temp/common/composables/useProcessEnv'
import { handleApiError } from '@/utils/errorHandler'
import TimecardsTable from '@/views/v2-temp/eod-closing/components/TimecardsTable.vue'
import ExpensesTable from '@/views/v2-temp/eod-closing/components/ExpensesTable.vue'
import DataTabs from '@/views/waiter/Data.vue'
import DailyAggregationStep from './components/DailyAggregationStep.vue'
import useEodClosingV2Aggregations from './composables/useEodClosingV2Aggregations'

export default {
  name: 'EodClosingV2',
  components: {
    TimecardsTable,
    ExpensesTable,
    DataTabs,
    DailyAggregationStep,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { route, router } = useRouter()
    const { isProduction } = useProcessEnv()
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)
    const date = ref(route.value.query.date || today)
    const step = ref(0)

    const steps = [
      'データセット管理',
      '経費管理',
      'ユーザー集計',
      '卓集計',
      '店舗集計',
      '完了',
    ]

    // APIエラーハンドラ（既存 eod-closing と同じ handleApiError パターン）
    const onApiError = error => handleApiError(error, { $toast: vm.$toast })

    const {
      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      getAggregations,
      reloadAggregation,
      refreshAggregation,
      enqueueGenerateBusinessRuleResults,
      generateBusinessRuleResult,
      fetchAvailableUsers,
      createUserAggregation,
      destroyUserAggregation,
    } = useEodClosingV2Aggregations(date, { handleApiError: onApiError })

    // シートの「保存」操作: 一括保存APIを呼ぶ
    const onSave = id => {
      enqueueGenerateBusinessRuleResults(id)
    }

    // 適用ユーザーの変更（ユーザー集計ステップから）
    const onFetchAvailableUsers = ({ id, callback }) => {
      fetchAvailableUsers(id, callback)
    }
    const onCreateUser = ({ id, userId, callback }) => {
      createUserAggregation(id, { userId, callback })
    }
    const onDestroyUser = ({
      aggregationId, id, userId, callback,
    }) => {
      destroyUserAggregation(aggregationId, { id, userId, callback })
    }

    // 日付変更時: 本番では未来日を弾き、URLクエリを同期し、集計を取り直す
    watch(date, newDate => {
      const dateObj = parseISO(newDate)
      const todayObj = parseISO(today)
      if (isProduction && isAfter(dateObj, todayObj)) {
        date.value = today

        return
      }

      if (route.value.query.date !== date.value) {
        router.replace({
          name: route.value.name,
          query: { ...route.value.query, date: date.value },
        })
      }
      getAggregations()
    })

    onMounted(() => {
      getAggregations()
    })

    return {
      date,
      today,
      step,
      steps,
      isProduction,

      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      reloadAggregation,
      refreshAggregation,
      generateBusinessRuleResult,
      onSave,
      onFetchAvailableUsers,
      onCreateUser,
      onDestroyUser,

      icons: {
        mdiCalendar,
        mdiCheckCircle,
      },
    }
  },
}
</script>

<style lang="scss">
.eod-closing-v2-stepper {
  background: none !important;
}
</style>
